import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { api, formatApiError, imgSrc } from "../lib/api";
import { money } from "../lib/shop";
import { useAuth } from "../context/AuthContext";
import { PieceSelector } from "../components/shop/PieceSelector";
import { DesignPicker, designText } from "../components/shop/DesignPicker";

const field = "w-full rounded-xl border border-[#EBD08A] bg-white px-4 py-3 outline-none focus:border-[#FF1F8E]";

const calc = (item, pieces, fulfillment) => {
  const extra = pieces.reduce((s, p) => s + (item.plus_sizes.includes(p.size) ? item.plus_size_fee : 0) + (p.personalization?.trim() ? item.personalization_fee : 0), 0);
  const shipping = fulfillment === "ship" ? item.shipping_fee : 0;
  return { extra, shipping, total: item.price + extra + shipping };
};

export default function ShopItemPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [pieces, setPieces] = useState([]);
  const [design, setDesign] = useState({});
  const [files, setFiles] = useState([]);
  const [fulfillment, setFulfillment] = useState("");
  const [c, setC] = useState({ name: "", phone: "", email: "" });
  const [hasDesigns, setHasDesigns] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/shop").then((r) => { const it = r.data.find((x) => x.id === id) || false; setItem(it); if (it) setPieces(it.pieces.map(() => ({}))); });
    api.get("/shop/designs").then((r) => setHasDesigns(r.data.length > 0)).catch(() => {});
  }, [id]);
  useEffect(() => { if (user && user.role === "customer") setC((v) => ({ ...v, name: v.name || user.name, email: v.email || user.email })); }, [user]);
  const t = useMemo(() => (item ? calc(item, pieces, fulfillment) : null), [item, pieces, fulfillment]);

  if (item === null) return <div className="p-16 text-center text-sm">Loading…</div>;
  if (!item) return <div className="p-16 text-center" data-testid="shop-item-missing">This item isn't available. <Link to="/shop" className="font-semibold text-[#FF1F8E]">Back to shop</Link></div>;

  const pay = async (e) => {
    e.preventDefault();
    const missing = pieces.findIndex((p) => !p.size || !p.color);
    if (missing >= 0) return toast.error(`Please pick a color and size for the ${item.pieces[missing].label}`);
    const d = designText(design, hasDesigns);
    if (!d) return toast.error("Please pick a design or describe your own");
    if (!fulfillment) return toast.error("Please choose shipping or pickup");
    setBusy(true);
    const fd = new FormData();
    fd.append("payload", JSON.stringify({ item_id: item.id, pieces, fulfillment, design: d, ...c, origin_url: window.location.origin }));
    files.forEach((f) => fd.append("images", f));
    try {
      const { data } = await api.post("/shop/checkout", fd);
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  };

  return (
    <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="shop-item-page">
      <Link to="/shop" className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF1F8E]"><ArrowLeft className="h-4 w-4" />All items</Link>
      <form onSubmit={pay} className="mt-6 grid gap-10 lg:grid-cols-[1fr_1.2fr]">
        <div className="lg:sticky lg:top-28 lg:self-start">
          {imgSrc(item) && <img src={imgSrc(item)} alt={item.name} className="aspect-square w-full rounded-[2rem] object-cover" />}
          <h1 className="mt-6 font-display text-3xl font-bold sm:text-4xl" data-testid="shop-item-name">{item.name}</h1>
          <p className="mt-1 font-display text-2xl font-black text-[#FF1F8E]">{money(item.price)}</p>
          {item.description && <p className="mt-3 text-black/70">{item.description}</p>}
          <details className="mt-5 rounded-2xl bg-[#FFF4F8] p-4 text-sm"><summary className="cursor-pointer font-semibold">See the Gildan color chart</summary>
            <img src="/work/gildan-color-chart.jpg" alt="Gildan crewneck sweatshirt color chart" className="mt-3 w-full rounded-xl" /></details>
        </div>
        <div className="space-y-6">
          {item.pieces.map((spec, i) => (
            <PieceSelector key={i} idx={i} spec={spec} item={item} value={pieces[i] || {}} onChange={(v) => setPieces(pieces.map((p, j) => (j === i ? v : p)))} />
          ))}
          <div><p className="mb-3 font-display text-lg font-bold">Design</p><DesignPicker value={design} onChange={setDesign} files={files} setFiles={setFiles} /></div>
          <div>
            <p className="mb-3 font-display text-lg font-bold">Delivery</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <button type="button" onClick={() => setFulfillment("ship")} data-testid="fulfillment-ship" className={`rounded-xl p-4 text-left text-sm font-semibold ${fulfillment === "ship" ? "bg-[#FF1F8E] text-white" : "ring-1 ring-[#EBD08A]"}`}>Ship to me · {money(item.shipping_fee)}</button>
              {item.pickup && <button type="button" onClick={() => setFulfillment("pickup")} data-testid="fulfillment-pickup" className={`rounded-xl p-4 text-left text-sm font-semibold ${fulfillment === "pickup" ? "bg-[#FF1F8E] text-white" : "ring-1 ring-[#EBD08A]"}`}>Free pickup</button>}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <input required placeholder="Full name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} className={field} data-testid="shop-name-input" />
            <input required type="tel" placeholder="Phone" value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} className={field} data-testid="shop-phone-input" />
            <input required type="email" placeholder="Email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} className={field} data-testid="shop-email-input" />
          </div>
          <div className="rounded-2xl bg-[#0E0B0D] p-6 text-white" data-testid="shop-total-box">
            <div className="flex justify-between text-sm text-white/70"><span>{item.name}</span><span>{money(item.price)}</span></div>
            {t.extra > 0 && <div className="mt-1 flex justify-between text-sm text-white/70"><span>Add-ons</span><span>{money(t.extra)}</span></div>}
            {t.shipping > 0 && <div className="mt-1 flex justify-between text-sm text-white/70"><span>Shipping</span><span>{money(t.shipping)}</span></div>}
            <div className="stitch-line my-4 opacity-60" />
            <div className="flex items-end justify-between"><span className="font-semibold">Total</span><span className="font-display text-3xl font-black text-[#EBD08A]" data-testid="shop-total">{money(t.total)}</span></div>
            <p className="mt-1 text-xs text-white/50">Sales tax, if any, is added at checkout.</p>
            <button disabled={busy} className="btn-pink mt-5 w-full !py-4 text-lg disabled:opacity-60" data-testid="shop-pay-btn">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-4 w-4" />}Pay {money(t.total)}
            </button>
            <p className="mt-3 text-center text-xs text-white/50">Secure checkout by Stripe · Card, Apple Pay & Google Pay</p>
          </div>
        </div>
      </form>
    </section>
  );
}
